<template>
  <div class="panel">
    <div class="header">
      <div class="headerText">
        <div class="title">{{ title }}</div>
        <div v-if="subtitle" class="muted" style="font-size:12px; margin-top:2px;">{{ subtitle }}</div>
      </div>
      <div v-if="showConnectionStatus" class="headerRight">
        <button
          v-if="showCancelAction"
          class="btn danger cancelBtn"
          :disabled="cancelDisabled"
          @click="cancelRun"
        >
          {{ cancelButtonLabel }}
        </button>
        <div v-else class="muted" style="font-size:12px;">
          {{ connected ? 'connected' : 'reconnecting' }}
        </div>
      </div>
    </div>
    <div v-if="cancelError" class="error" style="margin-top:6px; font-size:12px;">{{ cancelError }}</div>

    <div v-if="run" style="margin-top:10px;">
      <AgentRunProgress
        :status="String(run.status || '')"
        :startedAt="String(run.started_at || '')"
        :maxRuntimeSeconds="Number(run.max_runtime_seconds || 0) || 0"
        :toolCallsUsed="Number(run.tool_calls_used || 0) || 0"
        :maxToolCalls="Number(run.max_tool_calls || 0) || 0"
        :leadiqQueryCount="Number(run.leadiq_query_count || 0) || 0"
        :maxLeadiqQueries="Number(run.max_leadiq_queries || 0) || 0"
      />
    </div>

    <div class="feed" ref="feedEl" :style="{ maxHeight: `${feedHeight}px` }">
      <div v-if="visibleEvents.length === 0" class="muted" style="padding:10px;">No events yet.</div>

      <TransitionGroup name="feed-item">
        <div v-for="(ev, idx) in visibleEvents" :key="ev.id" class="feedRow">
          <div
            class="feedBody"
            :class="{ boundaryStart: boundaryKind(ev) === 'start', boundaryEnd: boundaryKind(ev) === 'end' }"
            :style="{ '--indent-px': `${indentPx(idx)}px`, paddingLeft: 'var(--indent-px)' }"
          >
            <div class="feedIcon">
              <HeroIcon
                v-if="iconForEvent(ev, idx).kind === 'agent'"
                :name="iconForEvent(ev, idx).name"
                :iconStyle="iconForEvent(ev, idx).style"
                :color="iconForEvent(ev, idx).color"
                :size="16"
                aria-hidden="true"
              />
              <AppIcon
                v-else
                :name="iconForEvent(ev, idx).key"
                :color="iconForEvent(ev, idx).color"
                :size="16"
              />
            </div>
            <div class="feedContent">
              <MarkdownRenderer v-if="isAssistantMessage(ev)" class="assistant" :source="String(ev.message || '')" />
              <span v-else class="feedLine" :title="ev.message">
                <template v-if="boundaryPart(ev)">
                  <span class="boundaryAgent">{{ boundaryPart(ev).agent }}</span>
                  <span class="boundarySuffix"> {{ boundaryPart(ev).suffix }}</span>
                  <span v-if="boundaryPart(ev).kind === 'start' && ev?.payload?.instruction" class="boundaryInstruction">
                    {{ String(ev.payload.instruction) }}
                  </span>
                </template>
                <template v-else>
                  {{ displayMessage(ev) }}
                </template>
              </span>
            </div>
          </div>
          <span class="feedTime">{{ shouldShowTime(idx) ? formatTime(ev.created_at) : '' }}</span>
        </div>
      </TransitionGroup>

      <div v-if="isInProgress && visibleEvents.length > 0" class="pulseRow">
        <PulseDot :size="12" color="var(--muted)" />
      </div>
    </div>

    <div v-if="showChat" class="chatBox">
      <input
        v-model="chatMessage"
        class="chatInput"
        :disabled="disabled || !runId"
        @keyup.enter="sendChat"
      />
      <button class="btn" :disabled="disabled || !runId || !chatMessage.trim()" @click="sendChat">Send</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { Ref, ComputedRef } from 'vue'
import { apiFetch } from '../services/api'
import {getAccessToken, getAuthMode} from '../services/auth'
import { useActionCounts } from '../services/actionCountsPoller'
import {
  computeAgentIconStack,
  isRestartEvent,
  isTerminalEvent,
  resolveEventIcon,
  type EventIcon,
} from '../services/agentRunIcons'
import AppIcon from './AppIcon.vue'
import AgentRunProgress from './AgentRunProgress.vue'
import HeroIcon from './HeroIcon.vue'
import MarkdownRenderer from './MarkdownRenderer.vue'
import PulseDot from './PulseDot.vue'

type AgentRunEvent = {
  id: string,
  kind: string,
  message: string,
  payload: any,
  created_at: string,
}

type AgentRunEventsResponse = {
  events: AgentRunEvent[]
}

type BoundaryPart = {
    kind: 'start' | 'end',
    agent: any,
    suffix: 'is running' | 'finished' | 'cancelled' | 'canceled' | 'errored' | null,
    depth: number,
    spanId: string,
    parentSpanId: string,
}

const props = defineProps({
  runId: { type: [String, Number], default: '' },
  title: { type: String, default: 'Activity' },
  subtitle: { type: String, default: 'Streaming live events from the agent run.' },
  showChat: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  showConnectionStatus: { type: Boolean, default: true },
  feedHeight: { type: [Number, String], default: 280 },
  agentName: { type: String, default: null },
  agentIconName: { type: String, default: null },
  agentIconStyle: { type: String, default: null },
  agentIconColor: { type: String, default: null },
})

const emit = defineEmits(['status', 'event'])

const events: Ref<AgentRunEvent[]> = ref([])
const chatMessage: Ref<string> = ref('')

const connected = ref(false)
const lastEventId = ref(null)
const cancelling = ref(false)
const cancelRequested = ref(false)
const cancelError = ref('')

const run = ref(null)
let runPollTimer = null

const feedEl = ref(null)
const feedAutoScroll = ref(true)

let es = null
let reconnectTimer = null

const runIdS: ComputedRef<string> = computed(() => String(props.runId || '').trim())

// Brand integration favicons are registered in iconRegistry as
// ``library: 'image'`` entries; consumers ask AppIcon for the key. See
// iconRegistry.ts. Outreach isn't registered there yet because no event
// currently uses it as a category — the system_log context-string lookup
// below remains as a one-off mapping until that's needed.

function boundaryKind(ev) {
  if (parseAgentRunBoundary(ev, 'start')) return 'start'
  if (parseAgentRunBoundary(ev, 'end')) return 'end'
  return null
}

// Per-event-kind heuristics (isRestartEvent / isTerminalEvent /
// resolveEventIcon / RAVEN_*) live in ``services/agentRunIcons.ts``.

// --- Event filtering (dedup stories) ---

const visibleEvents: ComputedRef<AgentRunEvent[]> = computed(() => {
  // Collect IDs of story events so we can suppress the duplicate emit_story tool event
  const storyMessages = new Set()
  for (const ev of events.value) {
    if (kindS(ev).toLowerCase() === 'story') {
      storyMessages.add((ev.message || '').trim())
    }
  }

  const out: AgentRunEvent[] = []

  // Track the currently-running agent boundaries ("<agent> is running" / "<agent> finished").
  // This is what drives indentation.
  const runningStack: { agent: string; spanId: string }[] = []

  const agentNameS: string = String(props.agentName || '').trim()
  let mainAgentEnded = false

  for (const ev of events.value) {
    if (isRestartEvent(ev)) {
      runningStack.length = 0
    }

    const part = boundaryPart(ev)
    if (part?.kind === 'end') {
      if (!part?.spanId && agentNameS && String(part?.agent || '').trim() === agentNameS) {
        mainAgentEnded = true
      }
      const d = Number(part.depth || 0) || 0
      while (runningStack.length > Math.max(0, d + 1)) runningStack.pop()
      popToBoundaryObjStack(runningStack, part)
    }

    out.push(ev)

    if (part?.kind === 'start') {
      const d = Number(part.depth || 0) || 0
      while (runningStack.length > Math.max(0, d)) runningStack.pop()
      const spanId = String(part.spanId || '').trim()
      runningStack.push({ agent: part.agent, spanId })
    }
  }

  return out.filter((ev) => {
    const kind = kindS(ev).toLowerCase()
    // Hide progress events (internal counter updates)
    if (kind === 'progress') return false
    // Always hide emit_story tool events — the story itself shows up separately
    if (kind === 'tool' && toolNameFromPayload(ev) === 'emit_story') return false
    return true
  })
})

const agentIconStack = computed(() =>
  computeAgentIconStack(visibleEvents.value, {
    name: props.agentIconName,
    style: props.agentIconStyle,
    color: props.agentIconColor,
  }),
)

function iconForEvent(ev: AgentRunEvent, idx: number): EventIcon {
  return resolveEventIcon({
    event: ev,
    index: idx,
    agentStack: agentIconStack.value,
    root: {
      name: props.agentIconName,
      style: props.agentIconStyle,
      color: props.agentIconColor,
    },
  })
}

// --- Indentation for nested sub-agent runs ---

const indentLevelMap: ComputedRef<number[]> = computed(() => {
  const evs = visibleEvents.value
  const map: number[] = new Array<number>(evs.length)
  const runningStack = []

  for (let i = 0; i < evs.length; i++) {
    const ev = evs[i]

    // On a restart event, clear the stack so subsequent events render at the base indent level.
    if (isRestartEvent(ev)) {
      runningStack.splice(0)
    }

    const parts = boundaryPart(ev)

    // Finish boundary should be shown at the parent indentation level.
    if (parts?.kind === 'end') {
      const d = Number(parts.depth || 0) || 0
      // Depth d means this agent lives at stack index d; stack length should be d+1 while it's running.
      while (runningStack.length > Math.max(0, d + 1)) runningStack.pop()
      popToBoundary(runningStack, parts)
    }

    map[i] = Math.max(0, runningStack.length)

    if (parts?.kind === 'start') {
      const d = Number(parts.depth || 0) || 0
      // Depth d is the parent-count (0 for main agent, 1 for direct sub-agent, etc.).
      // Before pushing the agent, ensure we have exactly d parents on the stack.
      while (runningStack.length > Math.max(0, d)) runningStack.pop()
      const spanId = String(parts.spanId || '').trim()
      if (spanId) {
        if (!runningStack.length || runningStack[runningStack.length - 1] !== spanId) runningStack.push(spanId)
      } else {
        if (runningStack[runningStack.length - 1] !== parts.agent) runningStack.push(parts.agent)
      }
    }
  }
  return map
})

function indentPx(idx: number): number {
  const lvl: number = Number(indentLevelMap.value?.[idx] || 0)
  // One nesting level should shift content by exactly the icon column + gap
  // so that the child icon left edge aligns with the parent's text start.
  return Math.min(140, lvl * 28)
}

function parseAgentRunBoundary(ev, which) {
  const msg = String(ev?.message || '').trim()
  if (!msg) return null
  const lower = msg.toLowerCase()

  // These are the only messages that should affect indentation.
  if (which === 'start') {
    if (!lower.endsWith(' is running')) return null
    return msg.slice(0, msg.length - ' is running'.length).trim()
  }
  if (which === 'end') {
    if (lower.endsWith(' finished')) return msg.slice(0, msg.length - ' finished'.length).trim()
    if (lower.endsWith(' cancelled') || lower.endsWith(' canceled')) {
      const suffix = lower.endsWith(' canceled') ? ' canceled' : ' cancelled'
      return msg.slice(0, msg.length - suffix.length).trim()
    }
    if (lower.endsWith(' errored')) return msg.slice(0, msg.length - ' errored'.length).trim()
  }
  return null
}

function boundaryPart(ev: AgentRunEvent): BoundaryPart | null {
  const msg = (ev.message || '').trim()
  if (!msg) return null
  const lower = msg.toLowerCase()

  const kind = kindS(ev).toLowerCase()
  const toolName = toolNameFromPayload(ev)
  const category = String(ev.payload?.tool?.category || '').toLowerCase()
  const isAgentTool = kind === 'tool' && (toolName === 'call_agent' || category === 'agent_tools')

  // Main-agent boundary events come through as system_log (not tool) today.
  // Keep them working without requiring span metadata.
  if (!isAgentTool && kind !== 'system_log') return null

  const agentNameFromPayload = typeof ev.payload?.agent_name === 'string' ? ev.payload.agent_name.trim() : ''
  if (kind === 'system_log') {
    // Only treat system_log as boundaries when it is clearly an agent boundary line.
    const depth = 0
    const agent = agentNameFromPayload || parseAgentRunBoundary(ev, lower.endsWith(' is running') ? 'start' : 'end')
    if (!agent) return null
    if (lower.endsWith(' is running')) return { kind: 'start', agent, suffix: 'is running', depth, spanId: '', parentSpanId: '' }
    if (lower.endsWith(' finished')) return { kind: 'end', agent, suffix: 'finished', depth, spanId: '', parentSpanId: '' }
    if (lower.endsWith(' cancelled') || lower.endsWith(' canceled')) {
      const suffix = lower.endsWith(' canceled') ? 'canceled' : 'cancelled'
      return { kind: 'end', agent, suffix, depth, spanId: '', parentSpanId: '' }
    }
    if (lower.endsWith(' errored')) return { kind: 'end', agent, suffix: 'errored', depth, spanId: '', parentSpanId: '' }
    return null
  }

  // For call_agent tool boundaries, use structured span metadata + tool.phase.
  const spanIdRaw = ev.payload?.span_id
  const spanId = typeof spanIdRaw === 'string' ? spanIdRaw.trim() : ''
  if (!spanId) return null

  const parentSpanRaw = ev.payload?.parent_span_id
  const parentSpanId = typeof parentSpanRaw === 'string' ? parentSpanRaw.trim() : ''

  // Depth is provided on call_agent tool events. Use it to keep siblings aligned.
  const depth = Number(ev.payload?.depth || 0) || 0

  const phase = String(ev.payload?.tool?.phase || '').trim().toLowerCase()
  if (phase !== 'start' && phase !== 'done') return null

  const agent = String(ev.payload?.agent_name || '').trim() || 'Sub-agent'
  return {
    kind: phase === 'start' ? 'start' : 'end',
    agent,
    suffix: phase === 'start' ? 'is running' : (agentDoneStateFromMessage(ev?.message) || 'finished'),
    depth,
    spanId,
    parentSpanId,
  }
}

function popToAgent(stack, agentName) {
  if (!agentName) return
  if (!stack.length) return
  if (stack[stack.length - 1] === agentName) {
    stack.pop()
    return
  }
  const idx = stack.lastIndexOf(agentName)
  if (idx >= 0) {
    stack.splice(idx)
  }
}

function popToBoundary(stack, parts) {
  const spanId = String(parts?.spanId || '').trim()
  if (spanId) {
    if (stack[stack.length - 1] === spanId) {
      stack.pop()
      return
    }
    const idx = stack.lastIndexOf(spanId)
    if (idx >= 0) stack.splice(idx)
    return
  }
  popToAgent(stack, parts?.agent)
}

function popToBoundaryObjStack(stack, part) {
  const spanId = String(part?.spanId || '').trim()
  if (spanId) {
    const top = stack.length ? stack[stack.length - 1] : null
    if (top && String(top.spanId || '').trim() === spanId) {
      stack.pop()
      return
    }
    for (let i = stack.length - 1; i >= 0; i--) {
      const s = String(stack[i]?.spanId || '').trim()
      if (s === spanId) {
        stack.splice(i)
        return
      }
    }
    return
  }

  const agentName = String(part?.agent || '').trim()
  if (!agentName) return
  const top = stack.length ? stack[stack.length - 1] : null
  if (top && String(top.agent || '').trim() === agentName) {
    stack.pop()
    return
  }
  for (let i = stack.length - 1; i >= 0; i--) {
    const a = String(stack[i]?.agent || '').trim()
    if (a === agentName) {
      stack.splice(i)
      return
    }
  }
}

function agentDoneStateFromMessage(msg: string): 'finished' | 'cancelled' | 'errored' | null {
  const m = (msg || '').trim().toLowerCase()
  if (!m) return null
  if (m.endsWith(' finished')) return 'finished'
  if (m.endsWith(' cancelled') || m.endsWith(' canceled')) return 'cancelled'
  if (m.endsWith(' errored')) return 'errored'
  return null
}

// --- Helpers ---

function kindS(ev: { kind: string }): string {
  return String(ev.kind || '').trim()
}

function isAssistantMessage(ev: { kind: string }) {
  return kindS(ev).toLowerCase() === 'assistant_message'
}

function toolNameFromPayload(ev) {
  const name = ev?.payload?.tool?.name
  return typeof name === 'string' ? name.trim() : ''
}

function extractToolName(message: string): string {
  const m = String(message || '').match(/^\[([^\]]+)\]\s*/)
  return m?.[1] ? String(m[1]).trim() : ''
}

function displayMessage(ev: { message?: any; kind: string }) {
  const msg = String(ev.message || '')
  if (kindS(ev).toLowerCase() !== 'tool') return msg
  const tool = extractToolName(msg)
  if (!tool) return msg
  return msg.replace(new RegExp(`^\\[${tool.replaceAll('\\\\', '\\\\\\\\').replaceAll(']', '\\]')}\\]\\s*`), '').trim()
}

function shouldShowTime(idx: number): boolean {
  const evs = visibleEvents.value
  // Always show first
  if (idx === 0) return true
  // Always show terminal state events (completed, failed, stopped)
  if (isTerminalEvent(evs[idx])) return true
  // For middle entries, show only if different from the one above
  const thisTime = formatTime(evs[idx]?.created_at)
  const prevTime = formatTime(evs[idx - 1]?.created_at)
  return thisTime !== prevTime
}

function normalizeRunStatus(v: string) {
  return v.trim().toLowerCase()
}

const runStatus: ComputedRef<string> = computed(() => run.value?.status || '')
const isInProgress = computed(() => {
  const st = normalizeRunStatus(runStatus.value)
  return st === 'queued' || st === 'running' || st === 'in_progress' || st === 'in-progress'
})
const showCancelAction: ComputedRef<boolean> = computed(() => Boolean(runIdS.value) && isInProgress.value)
const cancelDisabled: ComputedRef<boolean> = computed(() => props.disabled || cancelling.value || cancelRequested.value)
const cancelButtonLabel: ComputedRef<string> = computed(() => {
  if (cancelling.value) return 'Cancelling…'
  if (cancelRequested.value) return 'Cancel requested'
  return 'Cancel'
})

async function cancelRun(): Promise<void> {
  if (!runIdS.value || !showCancelAction.value || cancelDisabled.value) return
  cancelling.value = true
  cancelError.value = ''
  try {
    await apiFetch(`/agent_runs/${encodeURIComponent(runIdS.value)}/cancel`, {
      method: 'POST',
      body: { reason: 'user_request' },
    })
    cancelRequested.value = true
    const { refresh } = useActionCounts()
    await refresh({ silent: true })
    await loadRun()
    emitStatus()
  } catch (e) {
    cancelError.value = e?.message || 'Failed to request cancel'
  } finally {
    cancelling.value = false
  }
}

function formatTime(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

function lastEventCreatedAt() {
  const last = events.value.length ? events.value[events.value.length - 1] : null
  return last?.created_at || null
}

function emitStatus() {
  emit('status', {
    connected: !!connected.value,
    lastEventCreatedAt: lastEventCreatedAt(),
    runStatus: runStatus.value,
    isInProgress: !!isInProgress.value,
  })
}

async function loadRun() {
  if (!runIdS.value) {
    run.value = null
    return
  }
  try {
    run.value = await apiFetch(`/agent_runs/${runIdS.value}`)
  } catch {
    run.value = null
  }
}

function stopRunPolling() {
  if (runPollTimer) {
    clearInterval(runPollTimer)
    runPollTimer = null
  }
}

function startRunPolling() {
  stopRunPolling()
  runPollTimer = setInterval(async () => {
    if (!runIdS.value) return
    if (!isInProgress.value) return
    await loadRun()
    emitStatus()
  }, 2500)
}

function onFeedScroll() {
  const el = feedEl.value
  if (!el) return
  const threshold = 30
  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold
  feedAutoScroll.value = atBottom
}

async function scrollFeedToBottom(force = false) {
  await nextTick()
  const el = feedEl.value
  if (!el) return
  if (!force && !feedAutoScroll.value) return
  try {
    el.scrollTop = el.scrollHeight
  } catch {
  }
}

async function loadRecentEvents() {
  if (!runIdS.value) {
    events.value = []
    lastEventId.value = null
    run.value = null
    emitStatus()
    return
  }

  try {
    await loadRun()
    const resp = await apiFetch<AgentRunEventsResponse>(`/agent_runs/${runIdS.value}/events?limit=200`)
    events.value = resp?.events || []
    const last = events.value.length ? events.value[events.value.length - 1] : null
    lastEventId.value = last?.id || null
    emitStatus()
    await scrollFeedToBottom(true)
  } catch {
    events.value = []
    lastEventId.value = null
    run.value = null
    emitStatus()
  }
}

function closeSse() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  try {
    if (es) es.close()
  } catch {
  }
  es = null
  connected.value = false
  emitStatus()
}

async function openSse() {
  closeSse()
  const mode = getAuthMode()
  const token = getAccessToken()
  if (!token || !runIdS.value) return

  const after = lastEventId.value ? `after=${encodeURIComponent(lastEventId.value)}` : ''
  const tokenQuery = mode === 'legacy' ? `token=${encodeURIComponent(token)}` : ''
  const url = `/api/agent_runs/${runIdS.value}/events/stream?${tokenQuery}${(tokenQuery ? '&' : '') + after}`
  es = new EventSource(url)
  es.onopen = () => {
    connected.value = true
    emitStatus()
  }
  es.onmessage = async (e) => {
    if (!e?.data) return
    let parsed = null
    try {
      parsed = JSON.parse(e.data)
    } catch {
      return
    }
    if (!parsed?.id) return
    events.value = [...events.value, parsed]
    lastEventId.value = parsed.id

    if (parsed?.kind === 'progress' && parsed?.payload?.counters && run.value) {
      const c = parsed.payload.counters
      run.value = {
        ...run.value,
        tool_calls_used: c.tool_calls_used ?? run.value.tool_calls_used,
        leadiq_query_count: c.leadiq_query_count ?? run.value.leadiq_query_count,
        contacts_scanned: c.contacts_scanned ?? run.value.contacts_scanned,
        prospects_evaluated: c.prospects_evaluated ?? run.value.prospects_evaluated,
      }
    }

    emit('event', parsed)
    emitStatus()
    await scrollFeedToBottom(false)
  }
  es.onerror = async () => {
    connected.value = false
    emitStatus()
    closeSse()
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      openSse()
    }, 1500)
  }
}

async function sendChat() {
  const msg = String(chatMessage.value || '').trim()
  if (!msg || !runIdS.value) return
  if (props.disabled) return
  try {
    await apiFetch(`/agent_runs/${runIdS.value}/chat`, { method: 'POST', body: { message: msg } })
    chatMessage.value = ''
  } catch {
  }
}

watch(
  runIdS,
  async () => {
    cancelling.value = false
    cancelRequested.value = false
    cancelError.value = ''
    if (feedEl.value) {
      try {
        feedEl.value.removeEventListener('scroll', onFeedScroll)
      } catch {
      }
    }
    closeSse()
    stopRunPolling()
    events.value = []
    lastEventId.value = null
    await loadRecentEvents()
    openSse()
    startRunPolling()
    await nextTick()
    if (feedEl.value) {
      feedEl.value.addEventListener('scroll', onFeedScroll, { passive: true })
    }
  },
  { immediate: true }
)

watch(isInProgress, (inProgress) => {
  if (!inProgress) {
    cancelling.value = false
    cancelRequested.value = false
  }
})

onMounted(() => {
  startRunPolling()
})

onBeforeUnmount(() => {
  closeSse()
  stopRunPolling()
  if (feedEl.value) {
    try {
      feedEl.value.removeEventListener('scroll', onFeedScroll)
    } catch {
    }
  }
})
</script>

<style scoped>

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.headerText {
  min-width: 0;
}

.headerRight {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 28px;
  align-self: center;
}

.cancelBtn {
  min-height: 28px;
  padding: 0 10px;
  font-size: 12px;
}

.title {
  font-weight: 700;
}

.muted {
  color: var(--muted);
}

.error {
  color: var(--danger);
}

.feed {
  margin-top: 10px;
  overflow: auto;
}

.feedRow {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 0;
}

.feedBody {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  position: relative;
  padding: 5px 0;
}

.feedBody::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: var(--indent-px, 0px);
  pointer-events: none;
  opacity: 0.75;
  background-image: repeating-linear-gradient(
    to right,
    transparent 0,
    transparent 10px,
    rgba(148, 163, 184, 0.55) 10px,
    rgba(148, 163, 184, 0.55) 11px,
    transparent 11px,
    transparent 28px
  );
}

.feedIcon {
  flex: 0 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 1px;
}

.feedIconImg {
  width: 16px;
  height: 16px;
  border-radius: 2px;
  flex-shrink: 0;
}

.feedContent {
  flex: 1;
  min-width: 0;
}

.feedLine {
  min-width: 0;
  white-space: normal;
  word-break: break-word;
  color: var(--text);
  font-size: 13px;
  line-height: 1.4;
}

.assistant {
  min-width: 0;
}

.feedTime {
  flex: 0 0 auto;
  color: var(--muted);
  font-size: 11px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  padding-top: 6px;
}

.feedBody.boundaryStart .boundaryAgent,
.feedBody.boundaryEnd .boundaryAgent {
  font-weight: 800;
}

.boundaryInstruction {
  margin-left: 8px;
  color: #6b7280;
}

.boundarySuffix {
  margin-left: 4px;
}

.feedBody.boundaryStart .feedLine,
.feedBody.boundaryEnd .feedLine {
  color: var(--text);
}

/* Slide-in animation for new events */
.feed-item-enter-active {
  transition: all 0.3s ease-out;
}
.feed-item-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.feed-item-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.pulseRow {
  display: flex;
  justify-content: flex-start;
  padding: 10px 0 4px 4px;
}

.chatBox {
  margin-top: 10px;
  display: flex;
  gap: 8px;
}

.chatInput {
  flex: 1;
  border: 1px solid var(--border);
  background: var(--field-bg);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  color: var(--text);
}

.chatInput::placeholder {
  color: var(--muted);
  opacity: 0.85;
}
</style>
